import { gql } from '@/__generated__';
import { DeviceList_GetDevicesQuery } from '@/__generated__/graphql';

export type Device = DeviceList_GetDevicesQuery['devicesConnection']['nodes'][number];

export const DEVICE_LIST_SUPPORT_INFO_FRAGMENT = gql(`#graphql
  fragment DeviceList_SupportInfo on Device {
    id
    support {
      id
      mobileOperator
      batterySocMinLastDay
      batterySocSlope
      batterySocRemainDays
      simMegaCurrentMonth
      simMegaLastMonth
      hoursWithData
      status
      eventsCount
      lastMetricDate
      energization
      connectivity
      ratioBuffer
      ratioOutlier
      hasDGAConfiguration
      supportTags {
        id
        name
        color
      }
    }
  }
`);

export const DEVICE_LIST_DGA_INFO_FRAGMENT = gql(`#graphql
  fragment DeviceList_DGAInfo on Device {
    id
    profile {
      id
      sensor
    }
    dga {
      id
      ticket {
        id
        timestamp
      }
      configuration {
        id
        type
        code
        enabled
        frequency
        hour
        weekday
        day
        dataDelayInHours
      }
    }
  }
`);

export const DEVICE_LIST_INFO_FRAGMENT = gql(`#graphql
  fragment DeviceList_Info on Device {
    id
    profile {
      id
      type
      name
      sensor
      source
      sourceInfo
      operationalState
      sensor
      organization {
        id
        name
      }
      irrigation {
        id
        name
      }
    }
    hardwareConfiguration {
      usbExtraction
    }
    alerts {
      id
      basicConfig: basicConfigurations {
        id
        lastFlow {
          timestamp
          min
          max
          overflow
          unit
          precision
        }
        lastLevel {
          timestamp
          min
          max
          overflow
          unit
          precision
        }
      }
    }
    distributionLimits {
      maxFlow
      unit
      precision
    }
    dataConfiguration {
      deviceId
      state {
        customerMode
      }
    }
    metrics {
      id
      flow {
        lastData
      }
      level {
        lastData
      }
      velocity {
        lastData
      }
    }
  }
`);

export const DEVICE_LIST_METRICS_FRAGMENT = gql(`#graphql
  fragment DeviceList_Metrics on Device {
    id
    mainMetricView {
      id
      masterId
      enabled
    }
    profile {
      id
      sensor
      typeVersion
    }
    metrics {
      id
      availableFields
      flow {
        lastData
        lastAlertState
        unit
        precision
      }
      level {
        lastData
        lastAlertState
        unit
        precision
      }
      velocity {
        lastData
        lastAlertState
        unit
        precision
      }
      dopplerVelocity {
        lastData
        lastAlertState
        unit
        precision
      }
      accumulatedVolume {
        lastData
        lastAlertState
        unit
        precision
      }
      angle {
        lastData
        lastAlertState
        unit
        precision
      }
      storedVolume {
        lastData
        lastAlertState
        unit
        precision
      }
      position {
        lastData
        lastAlertState
        unit
        precision
      }
    }
  }
`);

export const DEVICE_LIST_MINICHARTS_FRAGMENT = gql(`#graphql
  fragment DeviceList_Minicharts on Device {
    chartMetrics: metrics(
      input: {
        top: 10,
        start: "-6h"
      }
    ) {
      id
      availableFields
      batterySoc {
        data
        unit
        precision
      }
      solarV {
        data
        unit
        precision
      }
    }
    groupedMetrics: devicesListMinichart {
      id
      flow {
        data
        unit
        precision
      }
      level {
        data
        unit
        precision
      }
      position {
        data
        unit
        precision
      }
    }
  }
`);

export const DEVICE_LIST_PICTURE_FRAGMENT = gql(`#graphql
  fragment DeviceList_Picture on Device {
    id
    regis {
      lastPhoto
    }
    profile {
      id
      name
    }
    metrics {
      id
      flow {
        lastData
        lastAlertState
        unit
        precision
      }
      level {
        lastData
        lastAlertState
        unit
        precision
      }
    }
  }
`);

export const DEVICE_LIST_SMA_INFO_FRAGMENT = gql(`#graphql
  fragment DeviceList_SMAInfo on Device {
    id
    smaConfig {
      id
      smaProcessId
      smaUnitId
      metricField
      smaDeviceId
      smaMetricName
      smaMetricUnit
      username
      password
      enabled
      schedule {
        frequency
        hour
        weekday
        day
      }
    }
  }
`);

const DEVICE_LIST_GET_DEVICES = gql(`#graphql
  query DeviceList_GetDevices(
    $dgaVariant: Boolean!,
    $smaVariant: Boolean!,
    $supportVariant: Boolean!,
    $input: DevicesInput,
    $pagination: PaginationInput,
  ) {
    devicesConnection(input: $input, pagination: $pagination) {
      pageInfo {
        count
        totalCount
        pageNumber
        limit
        offset
      }
      nodes {
        id
        profile {
          id
          type
          source
          sourceInfo
          operationalState
        }
        ...DeviceList_Info
        ...DeviceList_Metrics @skip(if: $dgaVariant)
        ...DeviceList_Minicharts @skip(if: $dgaVariant)
        ...DeviceList_Picture @skip(if: $dgaVariant)
        ...DeviceList_DGAInfo @include(if: $dgaVariant)
        ...DeviceList_SMAInfo @include(if: $smaVariant)
        ...DeviceList_SupportInfo @include(if: $supportVariant)
      }
    }
  }
`);

export default DEVICE_LIST_GET_DEVICES;
